import { ArrowRightOutlined, MinusCircleOutlined, PlusOutlined } from "@ant-design/icons";
import { keepPreviousData, useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Alert, App, Button, Card, DatePicker, Flex, Form, Input, InputNumber, Modal, Result, Segmented, Select, Skeleton, Space, Steps, Table, Tag, Tooltip, Typography } from "antd";
import type { Dayjs } from "dayjs";
import { useState } from "react";
import { useNavigate, useParams } from "react-router";
import { ApiError, api, unwrap, type Schemas } from "@/api/client";
import { useApplyFieldErrors, useErrorText } from "@/api/errors";
import { useAuth } from "@/auth/AuthProvider";
import { ScanInput } from "@/components/ScanInput";
import { useTabTitle, usePoll } from "@/components/tabs/WorkTabs";
import { useI18n } from "@/i18n";
import type { ScanResult } from "@/lib/scanSound";
import { useUrlFilter } from "@/lib/useUrlFilter";
import { ExportButton } from "@/components/list/ExportButton";
import { DateRangeFilter, SearchFilter, StatusFilter, useSearchFilter, useStatusFilter } from "@/components/list/filters";
import { useDateRange, useListPage } from "@/components/list/useListPage";
import { useActiveWarehouse, useWarehouses } from "@/lib/useWarehouses";
import { SkuSelect } from "../catalog/mapping";
import { parseSerials } from "./SerialImport";

type Transfer = Schemas["Transfer"];
type Line = NonNullable<Transfer["lines"]>[number];
type Scan = NonNullable<Transfer["scans"]>[number];
type Direction = "all" | "out" | "in";
const TRANSFER_STATUSES = ["picking", "ready", "shipped", "receiving", "done", "cancelled"] as const;
const TRANSFER_STATUS_CHOICES = ["active", ...TRANSFER_STATUSES, "all"] as const;
const TRANSFER_STATUS_PARAM: Record<(typeof TRANSFER_STATUS_CHOICES)[number], string | undefined> = {
  active: "picking,ready,shipped,receiving",
  picking: "picking",
  ready: "ready",
  shipped: "shipped",
  receiving: "receiving",
  done: "done",
  cancelled: "cancelled",
  all: undefined,
};
type NewTransfer = { fromWarehouseId: string; toWarehouseId: string; note?: string; lines: { skuId?: string; quantity?: number }[] };

const statusColor: Record<Transfer["status"], string> = {
  picking: "processing",
  ready: "cyan",
  shipped: "blue",
  receiving: "geekblue",
  done: "success",
  cancelled: "default",
};

export function TransferStatusTag({ status }: { status: Transfer["status"] }) {
  const { t } = useI18n();
  return <Tag color={statusColor[status]}>{t(`wms.tr.status.${status}`)}</Tag>;
}

/** Gudang > Transfer: stock moved between warehouses (Plan §6.8). */
export function Transfers() {
  const { t, formatDateTime, formatNumber } = useI18n();
  const { me, can } = useAuth();
  const navigate = useNavigate();
  const warehouses = useWarehouses();
  // The active warehouse's transfers, out of it and into it (Plan §6.14).
  const wh = useActiveWarehouse().current?.id ?? "";
  const [direction, setDirection] = useUrlFilter<Direction>("dir", "all", ["all", "out", "in"]);
  const status = useStatusFilter(TRANSFER_STATUS_CHOICES, "active");
  const search = useSearchFilter();
  const range = useDateRange();
  const page = useListPage();
  const [creating, setCreating] = useState(false);
  const tz = me?.workspace?.timezone;
  const statusParam = TRANSFER_STATUS_PARAM[status.value];
  const filters = {
    ...(wh ? { warehouseId: wh, ...(direction !== "all" ? { direction } : {}) } : {}),
    ...(statusParam ? { status: statusParam } : {}),
    ...search.query,
    ...range.query,
  };
  const list = useQuery({
    queryKey: ["transfers", filters, page.query],
    queryFn: () => unwrap(api.GET("/v1/transfers", { params: { query: { ...filters, ...page.query } } })),
    refetchInterval: usePoll(15_000),
    placeholderData: keepPreviousData,
  });

  return (
    <>
      <Flex justify="space-between" align="center" wrap gap={12} style={{ marginBottom: 16 }}>
        <Typography.Title level={2} style={{ margin: 0, fontSize: 28 }}>
          {t("wms.tr.title")}
        </Typography.Title>
        {can("warehouse.manage") && (
          <Button type="primary" icon={<PlusOutlined />} onClick={() => setCreating(true)} disabled={warehouses.active.length < 2}>
            {t("wms.tr.add")}
          </Button>
        )}
      </Flex>
      {warehouses.isSuccess && warehouses.active.length < 2 && <Alert type="info" showIcon style={{ marginBottom: 16 }} title={t("wms.tr.needTwo")} />}
      <Flex gap={12} wrap style={{ marginBottom: 16 }}>
        <Segmented<Direction>
          value={direction}
          onChange={setDirection}
          options={[
            { value: "all", label: t("orders.all") },
            { value: "out", label: t("wms.tr.out") },
            { value: "in", label: t("wms.tr.in") },
          ]}
        />
        <SearchFilter filter={search} placeholder={t("wms.tr.search")} />
        <StatusFilter
          filter={status}
          options={[
            { value: "active", label: t("list.openOnly") },
            ...TRANSFER_STATUSES.map((v) => ({ value: v, label: t(`wms.tr.status.${v}`) })),
            { value: "all" as const, label: t("list.allStatuses") },
          ]}
        />
        <DateRangeFilter range={range} />
        <div style={{ flex: 1 }} />
        <ExportButton
          path="/v1/transfers/export"
          query={filters}
          filename={t("wms.tr.title")}
          columns={[
            { key: "number", label: t("wms.in.number") },
            { key: "fromName", label: t("wms.tr.from") },
            { key: "toName", label: t("wms.tr.to") },
            { key: "status", label: t("common.status"), values: Object.fromEntries(TRANSFER_STATUSES.map((v) => [v, t(`wms.tr.status.${v}`)])) },
            { key: "units", label: t("wms.tr.quantity"), kind: "number" },
            { key: "shipped", label: t("wms.tr.shipped"), kind: "number" },
            { key: "received", label: t("wms.in.received"), kind: "number" },
            { key: "note", label: t("inventory.note") },
            { key: "shipNote", label: t("wms.tr.shipNote") },
            { key: "createdBy", label: t("wms.count.createdBy") },
            { key: "createdAt", label: t("wms.count.createdAt"), kind: "datetime" },
            { key: "shippedAt", label: t("wms.tr.shippedAt"), kind: "datetime" },
            { key: "doneAt", label: t("list.completedAt"), kind: "datetime" },
          ]}
        />
      </Flex>
      <Table<Transfer>
        rowKey="id"
        loading={list.isFetching && !list.data}
        dataSource={list.data?.items ?? []}
        pagination={page.pagination(list.data)}
        locale={{ emptyText: t("wms.tr.empty") }}
        onRow={(x) => ({ onClick: () => navigate(`/app/warehouse/transfers/${x.id}`), style: { cursor: "pointer" } })}
        columns={[
          { title: t("wms.in.number"), dataIndex: "number", width: 140, render: (v: string) => <Typography.Text strong className="z-num">{v}</Typography.Text> },
          {
            title: t("wms.tr.route"),
            render: (_, x) => (
              <Space size={6}>
                {x.fromName}
                <ArrowRightOutlined style={{ fontSize: 11 }} />
                {x.toName}
              </Space>
            ),
          },
          { title: t("common.status"), dataIndex: "status", width: 130, render: (s: Transfer["status"]) => <TransferStatusTag status={s} /> },
          {
            title: t("wms.tr.unitsCol"),
            width: 200,
            align: "right",
            render: (_, x) => (
              <span className="z-num">
                {formatNumber(x.units)} · {formatNumber(x.shipped)} · {formatNumber(x.received)}
              </span>
            ),
          },
          { title: t("wms.count.createdAt"), dataIndex: "createdAt", width: 170, render: (v: string) => formatDateTime(v, tz) },
        ]}
      />
      <Typography.Text type="secondary" style={{ fontSize: 12 }}>
        {t("wms.tr.unitsHelp")}
      </Typography.Text>
      <CreateTransfer open={creating} defaultFrom={wh} onClose={() => setCreating(false)} />
    </>
  );
}

function CreateTransfer({ open, defaultFrom, onClose }: { open: boolean; defaultFrom: string; onClose: () => void }) {
  const { t } = useI18n();
  const { message } = App.useApp();
  const errorText = useErrorText();
  const applyFieldErrors = useApplyFieldErrors();
  const queryClient = useQueryClient();
  const navigate = useNavigate();
  const warehouses = useWarehouses();
  const [form] = Form.useForm<NewTransfer>();
  const from = Form.useWatch("fromWarehouseId", form);
  const save = useMutation({
    mutationFn: (v: NewTransfer) =>
      unwrap(
        api.POST("/v1/transfers", {
          body: {
            fromWarehouseId: v.fromWarehouseId,
            toWarehouseId: v.toWarehouseId,
            note: v.note?.trim() ?? "",
            lines: v.lines.filter((l) => l.skuId && l.quantity).map((l) => ({ skuId: l.skuId!, quantity: l.quantity! })),
          },
        }),
      ),
    onSuccess: (r) => {
      for (const k of ["transfers", "inventory", "tasks"]) void queryClient.invalidateQueries({ queryKey: [k] });
      onClose();
      navigate(`/app/warehouse/transfers/${r.id}`);
    },
    onError: (err) => {
      if (!applyFieldErrors(form, err)) void message.error(errorText(err));
    },
  });
  // From one of the member's warehouses, to any warehouse.
  const label = (w: (typeof warehouses.active)[number]) => (w.wmsEnabled ? `${w.name} · WMS` : w.name);
  const fromOptions = warehouses.accessible.map((w) => ({ value: w.id, label: label(w) }));
  const options = warehouses.active.map((w) => ({ value: w.id, label: label(w) }));
  return (
    <Modal open={open} width={720} title={t("wms.tr.add")} okText={t("wms.tr.create")} cancelText={t("common.cancel")} confirmLoading={save.isPending} onOk={() => form.submit()} onCancel={onClose} destroyOnHidden>
      <Form form={form} layout="vertical" requiredMark={false} initialValues={{ fromWarehouseId: defaultFrom || undefined, lines: [{}] }} onFinish={(v) => save.mutate(v)}>
        <Flex gap={12} align="end">
          <Form.Item name="fromWarehouseId" label={t("wms.tr.from")} rules={[{ required: true, message: t("field.required") }]} style={{ flex: 1 }}>
            <Select options={fromOptions} />
          </Form.Item>
          <ArrowRightOutlined style={{ marginBottom: 32 }} />
          <Form.Item name="toWarehouseId" label={t("wms.tr.to")} rules={[{ required: true, message: t("field.required") }]} style={{ flex: 1 }}>
            <Select options={options.filter((o) => o.value !== from)} />
          </Form.Item>
        </Flex>
        <Typography.Paragraph type="secondary" style={{ marginTop: -8 }}>
          {t("wms.tr.reserveHelp")}
        </Typography.Paragraph>
        <Typography.Text strong>{t("wms.in.lines")}</Typography.Text>
        <Form.List name="lines">
          {(fields, { add, remove }) => (
            <div style={{ marginTop: 8 }}>
              {fields.map((f) => (
                <Flex key={f.key} gap={8} align="start">
                  <Form.Item name={[f.name, "skuId"]} style={{ flex: 1 }} rules={[{ required: true, message: t("field.required") }]}>
                    <SkuSelect />
                  </Form.Item>
                  <Form.Item name={[f.name, "quantity"]} rules={[{ required: true, message: t("field.required") }]}>
                    <InputNumber min={1} max={1_000_000} precision={0} placeholder={t("wms.in.quantity")} style={{ width: 130 }} />
                  </Form.Item>
                  {fields.length > 1 && <Button type="text" icon={<MinusCircleOutlined />} onClick={() => remove(f.name)} />}
                </Flex>
              ))}
              <Button type="dashed" icon={<PlusOutlined />} onClick={() => add({})} block>
                {t("wms.in.addLine")}
              </Button>
            </div>
          )}
        </Form.List>
        <Form.Item name="note" label={t("wms.in.note")} style={{ marginTop: 16 }}>
          <Input.TextArea rows={2} maxLength={300} />
        </Form.Item>
      </Form>
    </Modal>
  );
}

const stepOf: Record<Transfer["status"], number> = { picking: 0, ready: 1, shipped: 2, receiving: 2, done: 3, cancelled: 0 };

/** One transfer: pick, ship, receive, complete. */
export function TransferDetail() {
  const { transferID = "" } = useParams();
  const { t, formatDateTime } = useI18n();
  const { me, can } = useAuth();
  const { message, modal } = App.useApp();
  const errorText = useErrorText();
  const queryClient = useQueryClient();
  const navigate = useNavigate();
  const [shipping, setShipping] = useState(false);
  const [putaway, setPutaway] = useState<string | null>(null);
  const tz = me?.workspace?.timezone;
  const key = ["transfers", "detail", transferID];
  const tr = useQuery({
    queryKey: key,
    queryFn: () => unwrap(api.GET("/v1/transfers/{transferID}", { params: { path: { transferID } } })),
    retry: (n, err) => !(err instanceof ApiError && err.status === 404) && n < 2,
    refetchInterval: usePoll(15_000),
  });
  useTabTitle(tr.data?.number);
  const done = (res: Transfer) => {
    queryClient.setQueryData(key, res);
    for (const k of ["transfers", "inventory", "tasks", "locations"]) void queryClient.invalidateQueries({ queryKey: [k] });
  };
  const cancel = useMutation({
    mutationFn: () => unwrap(api.POST("/v1/transfers/{transferID}/cancel", { params: { path: { transferID } } })),
    onSuccess: done,
    onError: (err) => void message.error(errorText(err)),
  });
  const complete = useMutation({
    mutationFn: () => unwrap(api.POST("/v1/transfers/{transferID}/complete", { params: { path: { transferID } }, body: { acceptDifferences: true } })),
    onSuccess: (res) => {
      done(res.transfer);
      setPutaway(res.putawayTaskId ?? null);
      void message.success(t("wms.tr.completed"));
    },
    onError: (err) => void message.error(errorText(err)),
  });

  if (tr.isError) {
    return tr.error instanceof ApiError && tr.error.status === 404 ? <Result status="404" title={t("page.notFound.title")} /> : <Result status="error" title={errorText(tr.error)} />;
  }
  const x = tr.data;
  if (!x) return <Skeleton active />;
  const lines = x.lines ?? [];
  const arriving = x.status === "shipped" || x.status === "receiving";
  const diffs = lines.filter((l) => l.received !== l.shipped && l.shipped > 0);
  return (
    <>
      <Flex justify="space-between" align="center" wrap gap={12} style={{ marginBottom: 16 }}>
        <Space align="center" size={12} wrap>
          <Typography.Title level={2} className="z-num" style={{ margin: 0, fontSize: 28 }}>
            {x.number}
          </Typography.Title>
          <TransferStatusTag status={x.status} />
          <Typography.Text type="secondary">
            {x.fromName} → {x.toName} · {x.createdBy} · {formatDateTime(x.createdAt, tz)}
          </Typography.Text>
        </Space>
        <Space wrap>
          {(x.status === "picking" || x.status === "ready") && can("warehouse.manage") && (
            <Button
              danger
              loading={cancel.isPending}
              onClick={() =>
                modal.confirm({
                  title: t("wms.tr.cancel"),
                  content: t("wms.tr.cancelConfirm", { number: x.number }),
                  okButtonProps: { danger: true },
                  okText: t("wms.tr.cancel"),
                  cancelText: t("common.close"),
                  onOk: () => cancel.mutateAsync(),
                })
              }
            >
              {t("wms.tr.cancel")}
            </Button>
          )}
          {x.status === "picking" && x.taskId && <Button onClick={() => navigate(`/app/warehouse/tasks/${x.taskId}`)}>{t("wms.tr.openTask", { number: x.taskNumber ?? "" })}</Button>}
          {x.status === "ready" && (
            <Button type="primary" onClick={() => setShipping(true)}>
              {t("wms.tr.ship")}
            </Button>
          )}
          {arriving && (
            <Button
              type="primary"
              loading={complete.isPending}
              disabled={x.received === 0}
              onClick={() =>
                modal.confirm({
                  title: t("wms.tr.complete"),
                  width: 520,
                  content:
                    diffs.length === 0 ? (
                      t(x.toWms ? "wms.tr.completeBodyWms" : "wms.tr.completeBody")
                    ) : (
                      <Space orientation="vertical" size={8} style={{ width: "100%" }}>
                        <Typography.Text>{t("wms.tr.completeDiff")}</Typography.Text>
                        <Table<Line>
                          size="small"
                          rowKey="id"
                          pagination={false}
                          dataSource={diffs}
                          columns={[
                            { title: t("catalog.code"), dataIndex: "skuCode", render: (v: string) => <span className="z-num">{v}</span> },
                            { title: t("wms.tr.shipped"), dataIndex: "shipped", width: 80, align: "right" },
                            { title: t("wms.in.received"), dataIndex: "received", width: 80, align: "right" },
                            {
                              title: t("wms.in.difference"),
                              width: 90,
                              align: "right",
                              render: (_, l) => (
                                <Typography.Text type="warning" className="z-num">
                                  {l.received > l.shipped ? `+${l.received - l.shipped}` : l.received - l.shipped}
                                </Typography.Text>
                              ),
                            },
                          ]}
                        />
                        {diffs.some((l) => l.received < l.shipped) && <Typography.Text type="secondary">{t(x.toWms ? "wms.tr.completeShortWms" : "wms.tr.completeShort")}</Typography.Text>}
                        {diffs.some((l) => l.received > l.shipped) && <Typography.Text type="secondary">{t("wms.tr.completeOver")}</Typography.Text>}
                      </Space>
                    ),
                  okText: t("wms.tr.complete"),
                  cancelText: t("common.cancel"),
                  onOk: () => complete.mutateAsync(),
                })
              }
            >
              {t("wms.tr.complete")}
            </Button>
          )}
        </Space>
      </Flex>
      {x.status !== "cancelled" && (
        <Steps
          size="small"
          current={stepOf[x.status]}
          status={x.status === "done" ? "finish" : "process"}
          style={{ marginBottom: 16, maxWidth: 720 }}
          items={[
            { title: x.fromWms ? t("wms.tr.step.pick") : t("wms.tr.step.reserve") },
            { title: t("wms.tr.step.ship") },
            { title: t("wms.tr.step.receive") },
            { title: t("wms.tr.step.done") },
          ]}
        />
      )}
      {x.note && <Typography.Paragraph>{x.note}</Typography.Paragraph>}
      {x.shippedAt && (
        <Typography.Paragraph type="secondary">
          {t("wms.tr.shippedSummary", { by: x.shippedBy, at: formatDateTime(x.shippedAt, tz) })}
          {x.shipNote ? ` · ${x.shipNote}` : ""}
        </Typography.Paragraph>
      )}
      {putaway && (
        <Alert
          type="success"
          showIcon
          style={{ marginBottom: 16 }}
          title={t("wms.tr.putawayCreated")}
          action={<Button size="small" onClick={() => navigate(`/app/warehouse/tasks/${putaway}`)}>{t("wms.in.openPutaway")}</Button>}
        />
      )}
      {arriving && <ReceivePanel transfer={x} onDone={done} />}
      {arriving && (x.scans?.length ?? 0) > 0 && <ScanList transfer={x} onDone={done} />}
      <Card size="small" title={t("wms.in.lines")}>
        <Table<Line>
          size="small"
          rowKey="id"
          dataSource={lines}
          pagination={false}
          columns={[
            {
              title: t("catalog.code"),
              dataIndex: "skuCode",
              render: (v: string, l) => (
                <Space orientation="vertical" size={0}>
                  <Typography.Text strong className="z-num">
                    {v}
                  </Typography.Text>
                  <Typography.Text type="secondary" style={{ fontSize: 12 }}>
                    {l.skuName}
                  </Typography.Text>
                </Space>
              ),
            },
            {
              title: t("wms.tr.quantity"),
              width: 120,
              align: "right",
              render: (_, l) =>
                l.quantity < l.requested ? (
                  <Space size={4}>
                    <Typography.Text delete type="secondary" className="z-num">
                      {l.requested}
                    </Typography.Text>
                    <Typography.Text strong className="z-num">
                      {l.quantity}
                    </Typography.Text>
                  </Space>
                ) : (
                  <span className="z-num">{l.quantity}</span>
                ),
            },
            ...(x.fromWms ? [{ title: t("wms.wave.picked"), dataIndex: "picked", width: 100, align: "right" as const }] : []),
            { title: t("wms.tr.shipped"), dataIndex: "shipped", width: 100, align: "right" },
            { title: t("wms.in.received"), dataIndex: "received", width: 100, align: "right" },
            {
              title: t("wms.in.difference"),
              width: 110,
              align: "right",
              render: (_, l) => {
                if (x.status !== "receiving" && x.status !== "done") return <Typography.Text type="secondary">—</Typography.Text>;
                const d = l.received - l.shipped;
                return (
                  <Space size={4}>
                    <Typography.Text className="z-num" type={d === 0 ? "secondary" : "warning"}>
                      {d > 0 ? `+${d}` : d}
                    </Typography.Text>
                    {l.shortBooked > 0 && (
                      <Tooltip title={t("wms.tr.shortBookedHelp")}>
                        <Tag color="magenta">{t("wms.tr.shortBooked", { n: l.shortBooked })}</Tag>
                      </Tooltip>
                    )}
                  </Space>
                );
              },
            },
          ]}
        />
        {lines.some((l) => l.quantity < l.requested) && (
          <Typography.Text type="secondary" style={{ fontSize: 12 }}>
            {t("wms.tr.loweredHelp")}
          </Typography.Text>
        )}
      </Card>
      <ShipModal transfer={shipping ? x : null} onClose={() => setShipping(false)} onDone={done} />
    </>
  );
}

function ShipModal({ transfer, onClose, onDone }: { transfer: Transfer | null; onClose: () => void; onDone: (t: Transfer) => void }) {
  const { t } = useI18n();
  const { message } = App.useApp();
  const errorText = useErrorText();
  const [note, setNote] = useState("");
  // Serial SKUs leaving a WMS warehouse: their IMEIs (picking took them by count).
  const [serials, setSerials] = useState<Record<string, string>>({});
  const serialLines = transfer?.fromWms ? (transfer.lines ?? []).filter((l) => l.tracking === "serial" && l.quantity > 0) : [];
  const ready = serialLines.every((l) => parseSerials(serials[l.skuId] ?? "").length === l.quantity);
  const ship = useMutation({
    mutationFn: () =>
      unwrap(
        api.POST("/v1/transfers/{transferID}/ship", {
          params: { path: { transferID: transfer!.id } },
          body: {
            shipNote: note.trim(),
            ...(serialLines.length ? { serials: Object.fromEntries(serialLines.map((l) => [l.skuId, parseSerials(serials[l.skuId] ?? "")])) } : {}),
          },
        }),
      ),
    onSuccess: (res) => {
      onDone(res);
      void message.success(t("wms.tr.shipped1", { number: res.number }));
      setNote("");
      onClose();
    },
    onError: (err) => void message.error(errorText(err)),
  });
  return (
    <Modal
      open={transfer !== null}
      title={t("wms.tr.ship")}
      okText={t("wms.tr.ship")}
      cancelText={t("common.cancel")}
      confirmLoading={ship.isPending}
      okButtonProps={{ disabled: !ready }}
      onOk={() => ship.mutate()}
      onCancel={onClose}
      destroyOnHidden
    >
      <Typography.Paragraph type="secondary">{t("wms.tr.shipBody", { to: transfer?.toName ?? "" })}</Typography.Paragraph>
      {serialLines.map((l) => (
        <div key={l.skuId} style={{ marginBottom: 12 }}>
          <Typography.Text strong className="z-num">
            {l.skuCode}
          </Typography.Text>{" "}
          <Typography.Text type="secondary">
            {t("serial.transfer.help", { n: l.quantity })} ({parseSerials(serials[l.skuId] ?? "").length}/{l.quantity})
          </Typography.Text>
          <Input.TextArea
            rows={3}
            className="z-num"
            value={serials[l.skuId] ?? ""}
            onChange={(e) => setSerials((s) => ({ ...s, [l.skuId]: e.target.value }))}
            placeholder={t("serial.var.placeholder")}
          />
        </div>
      ))}
      <Input.TextArea rows={2} maxLength={300} value={note} onChange={(e) => setNote(e.target.value)} placeholder={t("wms.tr.shipNote")} />
    </Modal>
  );
}

/** Receiving at the destination: scan an SKU, then its quantity or serials. */
function ReceivePanel({ transfer, onDone }: { transfer: Transfer; onDone: (t: Transfer) => void }) {
  const { t } = useI18n();
  const { message, modal } = App.useApp();
  const errorText = useErrorText();
  const [line, setLine] = useState<Line | null>(null);
  const [qty, setQty] = useState<number | null>(1);
  const [serials, setSerials] = useState<string[]>([]);
  const [lotNo, setLotNo] = useState("");
  const [expires, setExpires] = useState<Dayjs | null>(null);
  const lines = transfer.lines ?? [];
  const left = line ? Math.max(0, line.shipped - line.received) : 0;
  const over = line && line.tracking !== "serial" ? Math.max(0, (qty ?? 0) - left) : 0;
  // Lots travel with units from a WMS source; from elsewhere, and for units
  // past what was shipped, they are asked.
  const askLot = line?.tracking === "lot" && transfer.toWms && (!transfer.fromWms || over > 0);
  const reset = () => {
    setLine(null);
    setQty(1);
    setSerials([]);
    setLotNo("");
    setExpires(null);
  };
  const receive = useMutation({
    mutationFn: (allowOver: boolean) =>
      unwrap(
        api.POST("/v1/transfers/{transferID}/receive", {
          params: { path: { transferID: transfer.id } },
          body: {
            skuId: line!.skuId,
            ...(line!.tracking === "serial" ? { serials } : { quantity: qty ?? 0 }),
            ...(askLot ? { lot: { no: lotNo.trim(), ...(expires ? { expiresOn: expires.format("YYYY-MM-DD") } : {}) } } : {}),
            ...(allowOver ? { allowOver } : {}),
          },
        }),
      ),
    onSuccess: (res) => {
      void message.success(t("wms.tr.counted1", { qty: line!.tracking === "serial" ? serials.length : (qty ?? 0), code: line!.skuCode }));
      onDone(res);
      reset();
    },
    onError: (err) => void message.error(errorText(err)),
  });
  const scan = (code: string): ScanResult => {
    if (line?.tracking === "serial") {
      const hit = lines.find((l) => l.skuCode.toUpperCase() === code.toUpperCase() || l.barcode === code);
      if (!hit) {
        setSerials((s) => (s.includes(code) ? s : [...s, code]));
        return true;
      }
    }
    const hit = lines.find((l) => l.skuCode.toUpperCase() === code.toUpperCase() || l.barcode === code);
    if (!hit || hit.shipped === 0) {
      void message.warning(t("error.not_on_transfer"));
      return false;
    }
    if (hit.id === line?.id && hit.tracking !== "serial") {
      setQty((q) => (q ?? 0) + 1);
      return true;
    }
    setLine(hit);
    setQty(1);
    setSerials([]);
    return true;
  };
  const submit = () => {
    if (over === 0) return receive.mutate(false);
    modal.confirm({
      title: t("wms.tr.overTitle"),
      content: t("wms.tr.overBody", { code: line!.skuCode, left, qty: qty ?? 0, over }),
      okText: t("wms.tr.overOk", { n: qty ?? 0 }),
      cancelText: t("common.cancel"),
      onOk: () => receive.mutateAsync(true),
    });
  };
  const ready = line && (line.tracking === "serial" ? serials.length > 0 : (qty ?? 0) > 0) && (!askLot || (lotNo.trim() && expires));
  return (
    <Card size="small" title={t("wms.tr.receiveTitle", { to: transfer.toName })} style={{ marginBottom: 16 }}>
      <Space orientation="vertical" size={8} style={{ width: "100%" }}>
        <Typography.Text type="secondary">{t(transfer.toWms ? "wms.tr.receiveHelpWms" : "wms.tr.receiveHelp")}</Typography.Text>
        <ScanInput onScan={scan} placeholder={t(line?.tracking === "serial" ? "wms.tr.scanSerial" : "wms.pack.scanItem")} />
        {line && (
          <Flex gap={12} wrap align="center">
            <Typography.Text strong className="z-num">
              {line.skuCode}
            </Typography.Text>
            <Typography.Text type="secondary">{t("wms.tr.left", { n: left })}</Typography.Text>
            {line.tracking === "serial" ? (
              <Space wrap>
                {serials.map((s) => (
                  <Tag key={s} closable onClose={() => setSerials((x) => x.filter((y) => y !== s))} className="z-num">
                    {s}
                  </Tag>
                ))}
              </Space>
            ) : (
              <InputNumber
                prefix={t("wms.in.quantity")}
                min={1}
                max={1_000_000}
                precision={0}
                value={qty}
                onChange={setQty}
                status={over > 0 ? "warning" : undefined}
                style={{ width: 180 }}
              />
            )}
            {askLot && (
              <>
                <Input placeholder={t("wms.in.lotNo")} value={lotNo} onChange={(e) => setLotNo(e.target.value)} style={{ width: 160 }} />
                <DatePicker placeholder={t("wms.in.expiresOn")} value={expires} onChange={setExpires} />
              </>
            )}
            <Button type="primary" disabled={!ready} loading={receive.isPending} onClick={submit}>
              {t("wms.tr.count")}
            </Button>
            <Button type="text" onClick={reset}>
              {t("common.cancel")}
            </Button>
          </Flex>
        )}
        {over > 0 && <Alert type="warning" showIcon title={t("wms.tr.overHint", { left, over })} />}
      </Space>
    </Card>
  );
}

/** Counted and not booked yet: a wrong or double scan is taken back here. */
function ScanList({ transfer, onDone }: { transfer: Transfer; onDone: (t: Transfer) => void }) {
  const { t, formatDateTime } = useI18n();
  const { me } = useAuth();
  const { message } = App.useApp();
  const errorText = useErrorText();
  const undo = useMutation({
    mutationFn: (scanID: string) => unwrap(api.DELETE("/v1/transfers/{transferID}/scans/{scanID}", { params: { path: { transferID: transfer.id, scanID } } })),
    onSuccess: (res) => {
      onDone(res);
      void message.success(t("wms.tr.scanUndone"));
    },
    onError: (err) => void message.error(errorText(err)),
  });
  return (
    <Card size="small" title={t("wms.tr.scansTitle")} style={{ marginBottom: 16 }}>
      <Typography.Paragraph type="secondary" style={{ marginBottom: 8 }}>
        {t("wms.tr.scansHelp")}
      </Typography.Paragraph>
      <Table<Scan>
        size="small"
        rowKey="id"
        pagination={false}
        dataSource={transfer.scans ?? []}
        columns={[
          { title: t("logs.time"), dataIndex: "at", width: 170, render: (v: string) => formatDateTime(v, me?.workspace?.timezone) },
          {
            title: t("catalog.code"),
            dataIndex: "skuCode",
            render: (v: string, x) => (
              <Space size={6} wrap>
                <span className="z-num">{v}</span>
                {x.serialNo && <Tag className="z-num">{x.serialNo}</Tag>}
                {x.lotNo && <Tag className="z-num">{x.lotNo}</Tag>}
                {x.over && <Tag color="warning">{t("wms.tr.overTag")}</Tag>}
              </Space>
            ),
          },
          { title: t("wms.in.quantity"), dataIndex: "quantity", width: 90, align: "right" },
          { title: t("logs.actor"), dataIndex: "actorName", width: 160 },
          {
            width: 90,
            render: (_, x) => (
              <Button size="small" loading={undo.isPending && undo.variables === x.id} onClick={() => undo.mutate(x.id)}>
                {t("wms.tr.undoScan")}
              </Button>
            ),
          },
        ]}
      />
    </Card>
  );
}
